import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Paper,
  InputBase,
  CircularProgress,
  Fade,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  TextField,
  InputAdornment,
  Avatar,
  Button,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import ImageIcon from "@mui/icons-material/Image";
import BusinessIcon from "@mui/icons-material/Business";
import DateRangeIcon from "@mui/icons-material/DateRange";
import SortIcon from "@mui/icons-material/Sort";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { getFilteredQualityData } from "../api/qualityAPI";
import "./Issues.scss";

const STORAGE_KEY = "lastSelectedProject";

const Issues = ({
  onIssueClick,
  onAddNew,
  compactView,
  isLoading,
  qualityData,
  selectedTab,
  setSelectedTab,
  searchQuery,
  setSearchQuery,
  selectedLocation,
  setSelectedLocation,
  projects,
  selectedProject,
  onProjectSelect,
  onRefresh,
  locationOptions = [],
  onExportToExcel, // Add the export function prop
  onExportAllSites, // Add the new exportAllSites function prop
}) => {
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [projectSearchQuery, setProjectSearchQuery] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [sortOrder, setSortOrder] = useState(() => {
    // Initialize sortOrder from localStorage or default to "newest"
    return localStorage.getItem("sortOrder") || "newest";
  });
  // New state for location dropdown
  const [locationMenuAnchorEl, setLocationMenuAnchorEl] = useState(null);
  const [locationSearchQuery, setLocationSearchQuery] = useState("");
  // New state to track processed items to prevent duplicates
  const [processedItems, setProcessedItems] = useState(new Set());

  // Load last selected project from localStorage on component mount
  useEffect(() => {
    if (!selectedProject && projects && projects.length > 0) {
      const savedProjectId = localStorage.getItem(STORAGE_KEY);
      if (savedProjectId) {
        const foundProject = projects.find(
          (p) =>
            p.ProjectId === savedProjectId ||
            p.ProjectId.toString() === savedProjectId
        );
        if (foundProject) {
          onProjectSelect(foundProject);
        }
      }
    }
  }, [projects, selectedProject, onProjectSelect]);

  // Save selected project to localStorage when it changes
  useEffect(() => {
    if (selectedProject) {
      localStorage.setItem(STORAGE_KEY, selectedProject.ProjectId.toString());
    }
  }, [selectedProject]);

  // Save sort order to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("sortOrder", sortOrder);
  }, [sortOrder]);

  // Reset processed items when project changes
  useEffect(() => {
    setProcessedItems(new Set());
  }, [selectedProject]);

  // Filter locations based on search query
  const filteredLocations = useMemo(() => {
    if (!locationOptions || locationOptions.length === 0) return [];

    if (!locationSearchQuery) return locationOptions;

    return locationOptions.filter((location) =>
      location.displayText
        .toLowerCase()
        .includes(locationSearchQuery.toLowerCase())
    );
  }, [locationOptions, locationSearchQuery]);

  // Filter data based on tab, search query, and selected location
  const filteredData = useMemo(() => {
    if (!qualityData) return [];

    // Get filtered data from API
    let data = getFilteredQualityData(
      qualityData,
      selectedTab,
      searchQuery,
      selectedLocation // Pass the selected location for filtering
    );

    // Sort data based on timestamp - this time across ALL dates
    if (data.length > 0) {
      // 1. Extract all items and their header associations
      const allItems = [];
      const headerMap = {};
      let currentHeader = null;

      data.forEach((item) => {
        if (item.type === "header") {
          currentHeader = item;
          headerMap[currentHeader.date] = item;
        } else {
          // Store the item with its date info
          allItems.push({
            item,
            dateText: currentHeader.date,
            dateId: currentHeader.id,
            timestamp: parseInt(item.id),
          });
        }
      });

      // 2. Sort all items regardless of their date header
      allItems.sort((a, b) => {
        return sortOrder === "newest"
          ? b.timestamp - a.timestamp
          : a.timestamp - b.timestamp;
      });

      // 3. Rebuild the list with headers inserted at date change boundaries
      data = [];
      let lastDateId = null;

      // This Set will help us prevent duplicates
      const seenItemIds = new Set();

      allItems.forEach(({ item, dateId }) => {
        // Skip duplicates
        if (seenItemIds.has(item.id)) return;
        seenItemIds.add(item.id);

        // Insert a header when the date changes
        if (lastDateId !== dateId) {
          lastDateId = dateId;
          data.push(headerMap[dateId.replace("header-", "")]);
        }
        data.push(item);
      });
    }

    return data;
  }, [qualityData, selectedTab, searchQuery, selectedLocation, sortOrder]);

  // Filtered projects for project selection
  const filteredProjects = useMemo(() => {
    if (!projects || !projects.length) return [];

    return projects.filter((project) =>
      project.ClientName.toLowerCase().includes(
        projectSearchQuery.toLowerCase()
      )
    );
  }, [projects, projectSearchQuery]);

  // Generate filtered data - optimized with useMemo to only recalculate when dependencies change
  const filteredItems = useMemo(() => {
    return getFilteredQualityData(
      qualityData,
      selectedTab,
      searchQuery,
      selectedLocation
    );
  }, [qualityData, selectedTab, searchQuery, selectedLocation]);

  // Update UI when qualityData changes (for real-time updates)
  useEffect(() => {
    // This empty useEffect ensures the component re-renders when qualityData changes
    // which helps with real-time status updates
  }, [qualityData]);

  // Handle refresh functionality
  const handleRefresh = async () => {
    if (!onRefresh) return;

    setRefreshing(true);
    try {
      // Reset processed items on manual refresh
      setProcessedItems(new Set());
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  // Handle tab changes
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Open project selection dialog
  const handleOpenProjectDialog = () => {
    setProjectDialogOpen(true);
    setProjectSearchQuery("");
  };

  // Select a project
  const handleSelectProject = (project) => {
    onProjectSelect(project);
    setProjectDialogOpen(false);
    // Reset processed items on project change
    setProcessedItems(new Set());
  };

  // Toggle sort order
  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "newest" ? "oldest" : "newest");
  };

  // Open location filter menu
  const handleLocationMenuOpen = (event) => {
    setLocationMenuAnchorEl(event.currentTarget);
    setLocationSearchQuery("");
  };

  // Close location filter menu
  const handleLocationMenuClose = () => {
    setLocationMenuAnchorEl(null);
  };

  // Select a location for filtering
  const handleLocationSelect = (displayText) => {
    setSelectedLocation(displayText);
    handleLocationMenuClose();
  };

  // Clear location filter
  const handleClearLocation = () => {
    setSelectedLocation("");
    handleLocationMenuClose();
  };

  // Render a card for each quality issue
  const renderQualityCard = (item) => {
    const status = item.status?.toLowerCase() || "unknown";
    const hasImages = item.images && Object.keys(item.images).length > 0;
    const hasResolutions =
      item.resolutions && Object.keys(item.resolutions).length > 0;

    return (
      <Paper
        key={item.id}
        className={`issue-item status-${status}`}
        onClick={() => onIssueClick(item)}
        elevation={1}
      >
        <Box className="issue-content">
          <Box className="issue-main">
            <Box className="issue-header">
              <Typography variant="subtitle1" className="issue-location">
                {highlightText(item.location, searchQuery)}
              </Typography>
              <Box className={`status-badge ${status}`}>{item.status}</Box>
            </Box>

            <Typography variant="body2" className="issue-description">
              {highlightText(item.description, searchQuery)}
            </Typography>

            <Box className="issue-meta">
              <Box className="meta-item reporter">
                <PersonIcon fontSize="small" />
                <Typography variant="caption">
                  {highlightText(item.by || "Unknown", searchQuery)}
                </Typography>
              </Box>

              <Box className="meta-item">
                <AccessTimeIcon fontSize="small" />
                <Typography variant="caption">
                  {item.timestamp ||
                    new Date(parseInt(item.id)).toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                </Typography>
              </Box>

              {hasImages && (
                <Box className="meta-item">
                  <ImageIcon fontSize="small" />
                  <Typography variant="caption">
                    {Object.keys(item.images).length}
                  </Typography>
                </Box>
              )}

              {hasResolutions && (
                <Box className="meta-item">
                  <MessageIcon fontSize="small" />
                  <Typography variant="caption">
                    {Object.keys(item.resolutions).length}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
    );
  };

  // Function to highlight text that matches search query
  const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    const parts = text.split(new RegExp(`(${searchTerm})`, "gi"));

    return (
      <span>
        {parts.map((part, i) =>
          part.toLowerCase() === searchTerm.toLowerCase() ? (
            <span key={i} className="highlight">
              {part}
            </span>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  // --- Add: Compute counts for All, Opened, Closed ---
  const tabCounts = useMemo(() => {
    let all = 0,
      opened = 0,
      closed = 0;
    if (qualityData && Object.keys(qualityData).length > 0) {
      Object.values(qualityData).forEach((dateGroup) => {
        (dateGroup.items || []).forEach((item) => {
          if (!item.status) return;
          all++;
          if (item.status.toLowerCase() === "open") opened++;
          if (item.status.toLowerCase() === "closed") closed++;
        });
      });
    }
    return { all, opened, closed };
  }, [qualityData]);

  return (
    <Box
      className={`quality-issues-container ${
        compactView ? "compact-view" : ""
      }`}
    >
      {/* Header section */}
      <Box className="app-header">
        {!isSearchActive ? (
          <>
            <Typography variant="h6" component="h1">
              Quality Report
            </Typography>
            <Box className="header-actions">
              <IconButton
                onClick={() => setIsSearchActive(true)}
                className="action-button"
              >
                <SearchIcon />
              </IconButton>
              <IconButton
                onClick={handleRefresh}
                className="action-button"
                disabled={refreshing}
              >
                {refreshing ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
              {/* Excel Export Button - wrap in span to fix MUI Tooltip with disabled button error */}
              <Tooltip title="Export to Excel" arrow>
                <span>
                  <IconButton
                    onClick={onExportToExcel}
                    className="action-button"
                    disabled={
                      !selectedProject || Object.keys(qualityData).length === 0
                    }
                  >
                    <FileDownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </>
        ) : (
          <Box className="search-header">
            <IconButton
              onClick={() => {
                setIsSearchActive(false);
                setSearchQuery("");
              }}
            >
              <CloseIcon />
            </IconButton>
            <InputBase
              className="search-input"
              placeholder="Search reports..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              fullWidth
            />
          </Box>
        )}
      </Box>

      {/* Combined filter row */}
      <Box className="filter-controls-row">
        {/* Project Selector */}
        <Button
          variant="contained"
          className="project-selector-button"
          onClick={handleOpenProjectDialog}
          startIcon={<BusinessIcon />}
          endIcon={<KeyboardArrowDownIcon />}
        >
          {selectedProject ? selectedProject.ClientName : "Select Project"}
        </Button>

        {/* Location Menu */}
        <Menu
          anchorEl={locationMenuAnchorEl}
          open={Boolean(locationMenuAnchorEl)}
          onClose={handleLocationMenuClose}
          PaperProps={{
            style: {
              maxHeight: 300,
              width: 350,
            },
          }}
          className="location-menu"
        >
          {/* Search box for locations */}
          <Box
            sx={{
              p: 1,
              position: "sticky",
              top: 0,
              bgcolor: "background.paper",
              zIndex: 1,
            }}
          >
            <TextField
              autoFocus
              placeholder="Search locations..."
              fullWidth
              variant="outlined"
              size="small"
              value={locationSearchQuery}
              onChange={(e) => setLocationSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: locationSearchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setLocationSearchQuery("")}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>

          {/* All Locations Option */}
          <MenuItem
            onClick={handleClearLocation}
            selected={!selectedLocation}
            className="location-menu-item all-locations"
          >
            <ListItemText primary="All Locations" />
            {!selectedLocation && (
              <CheckIcon fontSize="small" className="selected-icon" />
            )}
          </MenuItem>

          {/* Location Options */}
          {filteredLocations.length > 0 ? (
            filteredLocations.map((location) => (
              <MenuItem
                key={`${location.type}-${location.id}`}
                onClick={() => handleLocationSelect(location.displayText)}
                selected={selectedLocation === location.displayText}
                className={`location-menu-item ${
                  location.type === "floor"
                    ? "floor-location"
                    : location.type === "block"
                    ? "block-location"
                    : "point-location"
                }`}
              >
                <ListItemText
                  primary={location.displayText}
                  secondary={
                    location.type === "block"
                      ? "Block"
                      : location.type === "floor"
                      ? "Floor"
                      : "Point"
                  }
                />
                {selectedLocation === location.displayText && (
                  <CheckIcon fontSize="small" className="selected-icon" />
                )}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <ListItemText
                primary={
                  locationSearchQuery
                    ? "No matching locations"
                    : "No locations available"
                }
              />
            </MenuItem>
          )}
        </Menu>

        {/* Tabs section */}
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="fullWidth"
          className="quality-tabs"
        >
          <Tab label={`All (${tabCounts.all})`} value="All" disableRipple />
          <Tab
            label={`Opened (${tabCounts.opened})`}
            value="Opened"
            disableRipple
          />
          <Tab
            label={`Closed (${tabCounts.closed})`}
            value="Closed"
            disableRipple
          />
        </Tabs>

        {/* Sort button */}
        <Button
          className="sort-button"
          onClick={toggleSortOrder}
          startIcon={<SortIcon />}
        >
          {sortOrder === "newest" ? "Newest" : "Oldest"}
        </Button>
      </Box>

      {/* Main content */}
      {isLoading && Object.keys(qualityData).length === 0 ? (
        <Box className="loading-container">
          <CircularProgress />
          <Typography>Loading quality reports...</Typography>
        </Box>
      ) : (
        <Box className="issues-list">
          {filteredData.length > 0 ? (
            filteredData.map((item) =>
              item.type === "header" ? (
                <Box key={item.id} className="date-header">
                  <Typography
                    variant="caption"
                    className="date-text"
                    style={{
                      color: "#555",
                      fontSize: "12px",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      fontFamily: "Arial, sans-serif",
                      display: "inline-block",
                    }}
                  >
                    {item.date}
                  </Typography>
                </Box>
              ) : (
                renderQualityCard(item)
              )
            )
          ) : (
            <Box className="empty-state">
              <Typography variant="h6">No reports found</Typography>
              <Typography variant="body2">
                {selectedProject
                  ? "Add new reports using the + button"
                  : "Please select a project to view reports"}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Add button (FAB) */}
      {!compactView && selectedProject && (
        <Fade in={!compactView}>
          <IconButton
            className="add-button"
            color="primary"
            onClick={onAddNew}
            aria-label="add new report"
          >
            <AddIcon />
          </IconButton>
        </Fade>
      )}

      {/* Project Selection Dialog */}
      <Dialog
        open={projectDialogOpen}
        onClose={() => setProjectDialogOpen(false)}
        className="project-dialog"
        fullWidth
        maxWidth="sm"
      >
        <Box className="dialog-header">
          <Typography variant="h6">Select Project</Typography>
          <IconButton
            onClick={() => setProjectDialogOpen(false)}
            className="close-button"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent className="dialog-content">
          <TextField
            autoFocus
            placeholder="Search projects..."
            fullWidth
            variant="outlined"
            value={projectSearchQuery}
            onChange={(e) => setProjectSearchQuery(e.target.value)}
            className="project-search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: projectSearchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setProjectSearchQuery("")}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* Export All Sites Option */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              mb: 2,
            }}
          >
            <Button
              onClick={onExportAllSites}
              startIcon={<FileDownloadIcon />}
              sx={{
                background: "linear-gradient(90deg, #fdd34d 80%, #fbbf24 100%)",
                color: "#7c4700",
                fontWeight: 700,
                fontSize: 17,
                letterSpacing: 0.2,
                borderRadius: "10px !important",
                width: "100%",
                py: 2,
                boxShadow: "0 2px 8px rgba(253,211,77,0.10)",
                mb: 1.5,
                textTransform: "none",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #fbbf24 80%,rgb(255, 173, 31) 100%)",
                  color: "#7c4700",
                  boxShadow: "0 4px 16px rgba(253,211,77,0.18)",
                },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 0,
                minHeight: 0,
              }}
            >
              <Box sx={{ textAlign: "left" }}>
                Export All Sites Data
                <Typography
                  sx={{
                    color: "#a16207",
                    fontSize: 13,
                    fontWeight: 400,
                    mt: 0.5,
                  }}
                >
                  Download all sites' quality issues as Excel
                </Typography>
              </Box>
            </Button>
          </Box>

          {filteredProjects.length > 0 ? (
            <List className="project-list">
              {filteredProjects.map((project) => {
                const isSelected =
                  selectedProject?.ProjectId === project.ProjectId;
                const firstLetter = project.ClientName.charAt(0).toUpperCase();

                return (
                  <ListItem
                    key={project.ProjectId}
                    button
                    onClick={() => handleSelectProject(project)}
                    className={`project-item ${isSelected ? "selected" : ""}`}
                  >
                    <Avatar className="project-avatar">{firstLetter}</Avatar>
                    <ListItemText
                      primary={project.ClientName}
                      secondary={project.Address || "No address available"}
                    />
                    {isSelected && <CheckIcon className="check-icon" />}
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Box className="no-projects">
              <Typography>No projects found</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// Helper icon component for message indicator
const MessageIcon = ({ fontSize }) => (
  <svg
    width={fontSize === "small" ? 16 : 24}
    height={fontSize === "small" ? 16 : 24}
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
  </svg>
);

export default Issues;
